import { z } from 'zod';
import { isValidGtin, normaliseGtin } from '../ids/index.js';

// ---------------------------------------------------------------------------------------
// SKU canonical form and validation (vault Core Model, accepted change 1; plan §12)

/** Whitespace of every kind (incl. NBSP) and invisible format characters (zero-width, BOM). */
const INVISIBLE_RE = /[\s\p{Cf}]/gu;

/** Longest SKU accepted, after canonicalising. */
export const SKU_MAX_LENGTH = 64;

/**
 * Characters a SKU may not contain: they separate fields in CSV round-trips, paths,
 * search syntax and people's notes (`,` `;` `|` `/` `\` `"`). Control characters are
 * refused separately.
 */
export const SKU_RESERVED_CHARACTERS = [',', ';', '|', '/', '\\', '"'] as const;

/**
 * The canonical form a SKU is unique on: trimmed, **every** whitespace character removed
 * (spaces, tabs, NBSP, zero-width spaces, BOM, other invisible format characters) and
 * uppercased. Idempotent. Two SKUs that canonicalise alike are the same SKU.
 */
export function canonicaliseSku(value: string): string {
  return value.replace(INVISIBLE_RE, '').toUpperCase();
}

/** One finding about a SKU. `index` points into the canonical form. */
export type SkuIssue =
  | { code: 'empty' }
  | { code: 'too_long'; length: number; max: number }
  | { code: 'non_ascii'; chars: string[] }
  | { code: 'control_character'; chars: string[] }
  | { code: 'reserved_character'; chars: string[] }
  | { code: 'scientific_notation' }
  | { code: 'look_alike'; char: string; index: number; looksLike: string };

/** Result of {@link validateSku}. */
export interface SkuValidation {
  /** The canonical SKU (see {@link canonicaliseSku}). */
  canonical: string;
  /** True when there are no errors (warnings do not block). */
  valid: boolean;
  /** Blocking problems. */
  errors: SkuIssue[];
  /** Things a person should look at (look-alike characters). */
  warnings: SkuIssue[];
}

const LETTER_RE = /[A-Z]/;
const DIGIT_RE = /\d/;
const LOOK_ALIKES: Record<string, { neighbour: RegExp; looksLike: string }> = {
  O: { neighbour: DIGIT_RE, looksLike: '0' },
  I: { neighbour: DIGIT_RE, looksLike: '1' },
  L: { neighbour: DIGIT_RE, looksLike: '1' },
  '0': { neighbour: LETTER_RE, looksLike: 'O' },
  '1': { neighbour: LETTER_RE, looksLike: 'I' },
};

function unique(chars: Iterable<string>): string[] {
  return [...new Set(chars)];
}

/**
 * Validates a SKU (canonicalising it first).
 *
 * Errors: empty; longer than {@link SKU_MAX_LENGTH}; non-ASCII characters; control
 * characters; reserved separators ({@link SKU_RESERVED_CHARACTERS}); a number mangled into
 * scientific notation by a spreadsheet (`1.23457E+12`).
 *
 * Warnings: look-alike characters that sit among the other kind of character: a letter
 * `O`, `I` or `L` next to a digit (`1O5`), or a digit `0` or `1` next to a letter (`B0X`).
 * Use {@link lookAlikeKey} to find two SKUs that differ only by such characters.
 */
export function validateSku(value: string): SkuValidation {
  const canonical = canonicaliseSku(value);
  const errors: SkuIssue[] = [];
  const warnings: SkuIssue[] = [];
  const chars = [...canonical];

  if (chars.length === 0) errors.push({ code: 'empty' });
  if (chars.length > SKU_MAX_LENGTH) {
    errors.push({ code: 'too_long', length: chars.length, max: SKU_MAX_LENGTH });
  }
  const nonAscii = unique(chars.filter((c) => c.codePointAt(0)! > 0x7f));
  if (nonAscii.length > 0) errors.push({ code: 'non_ascii', chars: nonAscii });
  const control = unique(
    chars.filter((c) => {
      const cp = c.codePointAt(0)!;
      return cp < 0x20 || cp === 0x7f;
    }),
  );
  if (control.length > 0) errors.push({ code: 'control_character', chars: control });
  const reserved = unique(
    chars.filter((c) => (SKU_RESERVED_CHARACTERS as readonly string[]).includes(c)),
  );
  if (reserved.length > 0) errors.push({ code: 'reserved_character', chars: reserved });
  if (isScientificNotation(canonical)) errors.push({ code: 'scientific_notation' });

  chars.forEach((c, i) => {
    const rule = LOOK_ALIKES[c];
    if (!rule) return;
    const prev = chars[i - 1] ?? '';
    const next = chars[i + 1] ?? '';
    if (rule.neighbour.test(prev) || rule.neighbour.test(next)) {
      warnings.push({ code: 'look_alike', char: c, index: i, looksLike: rule.looksLike });
    }
  });

  return { canonical, valid: errors.length === 0, errors, warnings };
}

/**
 * A key under which look-alike characters collapse (`O`→`0`, `I` and `L`→`1`). Two
 * different canonical SKUs with the same key probably name the same thing mistyped.
 */
export function lookAlikeKey(sku: string): string {
  return canonicaliseSku(sku).replace(/O/g, '0').replace(/[IL]/g, '1');
}

// ---------------------------------------------------------------------------------------
// Scientific notation (plan §12: barcodes corrupted by a spreadsheet)

// mantissa: digits, optional decimal separator (`.` or `,`), digits; then E, sign, digits.
const SCI_RE = /^[+-]?(\d*)([.,]?)(\d*)[eE]([+-]?)(\d+)$/;

/**
 * True when `value` looks like a number a spreadsheet rewrote in scientific notation:
 * `1.23457E+12`, `6.0015E+12`, `6,0015E+12`, `1E+12`, `1.5e12`, with any whitespace.
 * The mantissa needs at least one digit, and the value needs a decimal separator in the
 * mantissa **or** an explicit exponent sign, so a plain code such as `2E10` is not taken
 * for one. Such a value has lost digits and cannot be repaired from itself.
 */
export function isScientificNotation(value: string): boolean {
  const m = SCI_RE.exec(value.replace(INVISIBLE_RE, ''));
  if (!m) return false;
  const [, intPart, sep, fracPart, expSign] = m;
  if (`${intPart}${fracPart}` === '') return false;
  return sep !== '' || expSign !== '';
}

// ---------------------------------------------------------------------------------------
// Identifiers

/**
 * Kinds of product identifier. A product has many (vault change 1); its ENP id is the
 * permanent identity and never one of these.
 * - `sku`: the business's own code (canonical: {@link canonicaliseSku}).
 * - `gtin`: a barcode (canonical: 14 digits, check digit valid).
 * - `channel_id`: a sales channel's id for the product (exact, per channel).
 * - `supplier_code`: a supplier's code (canonical like a SKU).
 * - `legacy`: an id from a system the account switched from (exact).
 */
export const IDENTIFIER_KINDS = ['sku', 'gtin', 'channel_id', 'supplier_code', 'legacy'] as const;
/** One of {@link IDENTIFIER_KINDS}. */
export type IdentifierKind = (typeof IDENTIFIER_KINDS)[number];
/** zod schema for an identifier kind. */
export const identifierKindSchema = z.enum(IDENTIFIER_KINDS);

/** Result of {@link canonicalIdentifier}. */
export type CanonicalIdentifier =
  | {
      ok: true;
      kind: IdentifierKind;
      /** The value as given. */
      value: string;
      /** The form uniqueness and lookups use. */
      canonical: string;
      /** Non-blocking findings (SKU look-alikes). */
      warnings: SkuIssue[];
    }
  | {
      ok: false;
      kind: IdentifierKind;
      value: string;
      reason: 'empty' | 'invalid_sku' | 'scientific_notation' | 'invalid_gtin';
      /** For `invalid_sku`: the blocking problems. */
      issues: SkuIssue[];
    };

/**
 * The canonical value of an identifier, or why it has none.
 * - `sku`: {@link validateSku}; any error → `invalid_sku` (with the issues).
 * - `gtin`: whitespace removed; scientific notation → `scientific_notation`; a failed
 *   GS1 check digit or wrong length → `invalid_gtin`; else padded to 14 digits.
 * - `supplier_code`: canonicalised like a SKU; scientific notation is refused.
 * - `channel_id`, `legacy`: trimmed, otherwise exact (external ids are case-sensitive).
 */
export function canonicalIdentifier(kind: IdentifierKind, value: string): CanonicalIdentifier {
  const fail = (
    reason: 'empty' | 'invalid_sku' | 'scientific_notation' | 'invalid_gtin',
    issues: SkuIssue[] = [],
  ): CanonicalIdentifier => ({ ok: false, kind, value, reason, issues });
  const ok = (canonical: string, warnings: SkuIssue[] = []): CanonicalIdentifier => ({
    ok: true,
    kind,
    value,
    canonical,
    warnings,
  });

  if (value.replace(INVISIBLE_RE, '') === '') return fail('empty');
  switch (kind) {
    case 'sku': {
      const v = validateSku(value);
      return v.valid ? ok(v.canonical, v.warnings) : fail('invalid_sku', v.errors);
    }
    case 'gtin': {
      const digits = value.replace(INVISIBLE_RE, '');
      if (isScientificNotation(digits)) return fail('scientific_notation');
      if (!isValidGtin(digits)) return fail('invalid_gtin');
      return ok(normaliseGtin(digits));
    }
    case 'supplier_code': {
      if (isScientificNotation(value)) return fail('scientific_notation');
      return ok(canonicaliseSku(value));
    }
    case 'channel_id':
    case 'legacy':
      return ok(value.trim());
  }
}
